import { io } from 'socket.io-client';
import { getApiBaseUrl } from './api';

/**
 * Tiny wrapper around socket.io-client used by the SocialApps Inbox view.
 *
 *   - One persistent connection per (accessToken) — re-used across mounts.
 *   - JWT passed via the `auth` handshake field, matching the gateway's
 *     `socket.handshake.auth.token` lookup on the server.
 *   - Auto-reconnect (socket.io default) so brief network blips don't drop
 *     events.
 *
 * Usage (from a useEffect):
 *   const off = subscribeInboxMessages(accessToken, (event) => { ... });
 *   return off;  // unsubscribes the listener on cleanup
 *
 * The connection itself is shared, so subscribing/unsubscribing only
 * adds/removes a listener — not the underlying socket. The socket lives
 * for the lifetime of the tab.
 */

let socket = null;
let currentToken = null;

function getOrCreateSocket(accessToken) {
    if (!accessToken) return null;
    if (socket && currentToken === accessToken) return socket;

    if (socket) {
        socket.disconnect();
        socket = null;
    }

    const base = getApiBaseUrl();
    socket = io(base, {
        auth: { token: accessToken },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
    });
    currentToken = accessToken;
    return socket;
}

/** Subscribe to `inbox:message` events. Returns an unsubscribe function. */
export function subscribeInboxMessages(accessToken, handler) {
    const s = getOrCreateSocket(accessToken);
    if (!s) return () => {};
    s.on('inbox:message', handler);
    return () => { s.off('inbox:message', handler); };
}

/** Tear down the shared socket — call on logout. */
export function disconnectInboxSocket() {
    if (socket) {
        socket.disconnect();
        socket = null;
        currentToken = null;
    }
}
